import { useCallback, useEffect, useRef, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { label as fmtLabel, fmtDate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { EmptyState } from "@/components/shared";
import { toast } from "sonner";
import {
  UploadCloud,
  File as FileIcon,
  FileText,
  FileSpreadsheet,
  Image as ImageIcon,
  Film,
  Music,
  Download,
  Eye,
  Trash2,
  Archive,
  ArchiveRestore,
  MoreVertical,
  RefreshCw,
  Share2,
  ShieldAlert,
  ShieldCheck,
  Clock,
  Loader2,
  History,
  Pencil,
  AlertTriangle,
} from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_PRESETS = {
  candidate: [
    "resume",
    "portfolio",
    "certification",
    "identification",
    "work_authorization",
    "assessment",
    "offer_letter",
    "photo",
    "other",
  ],
  client: [
    "contract",
    "msa",
    "sow",
    "po",
    "rate_card",
    "proposal",
    "invoice",
    "tax",
    "logo",
    "other",
  ],
  contractor: [
    "onboarding",
    "compliance",
    "license",
    "certification",
    "timesheet",
    "expense",
    "identification",
    "right_to_work",
    "signed_form",
    "banking",
    "tax",
    "other",
  ],
  job: ["job_description", "intake", "submission", "other"],
  submission: ["submission", "resume", "other"],
  interview: ["interview", "feedback", "other"],
  offer: ["offer_letter", "agreement", "other"],
  placement: ["placement", "warranty", "replacement", "other"],
  lead: ["proposal", "agreement", "presentation", "collateral", "other"],
  opportunity: ["proposal", "agreement", "presentation", "collateral", "other"],
  employee: [
    "hr_document",
    "policy",
    "offer_letter",
    "performance",
    "attendance",
    "photo",
    "banking",
    "tax",
    "other",
  ],
  invoice: ["invoice", "tax", "payment", "other"],
  timesheet: ["timesheet", "expense", "other"],
  company: [
    "logo",
    "branding",
    "email_signature",
    "template",
    "marketing",
    "other",
  ],
  announcement: ["image", "banner", "gallery", "attachment", "other"],
};

const IMG = ["png", "jpg", "jpeg", "webp", "svg", "gif"];
const SHEET = ["xls", "xlsx", "csv"];
const VIDEO = ["mp4", "mov", "webm"];
const AUDIO = ["mp3", "wav", "m4a"];

function iconFor(ext) {
  ext = (ext || "").toLowerCase();
  if (IMG.includes(ext)) return ImageIcon;
  if (SHEET.includes(ext)) return FileSpreadsheet;
  if (VIDEO.includes(ext)) return Film;
  if (AUDIO.includes(ext)) return Music;
  if (["pdf", "doc", "docx", "txt", "rtf", "ppt", "pptx"].includes(ext))
    return FileText;
  return FileIcon;
}

function fmtSize(bytes) {
  if (!bytes && bytes !== 0) return "—";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

export default function FileManager({
  recordType,
  recordId,
  canWrite = false,
  title = "Documents & Media",
}) {
  const { can } = useAuth();
  const canPurge = can("admin:access");
  const [files, setFiles] = useState(null);
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef(null);
  const replaceRef = useRef(null);
  const [replaceTarget, setReplaceTarget] = useState(null);

  // upload dialog
  const [pending, setPending] = useState([]); // {file, name, dupe}
  const [meta, setMeta] = useState({
    category: "",
    sensitivity: "standard",
    description: "",
    tags: "",
    expiry_date: "",
  });
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState({});

  const [editTarget, setEditTarget] = useState(null);
  const [editMeta, setEditMeta] = useState({});
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [purge, setPurge] = useState(false);
  const [shareTarget, setShareTarget] = useState(null);
  const [share, setShare] = useState({
    audience: "client",
    recipient: "",
    expires_in_days: 7,
  });
  const [versionsTarget, setVersionsTarget] = useState(null);

  const categories = CATEGORY_PRESETS[recordType] || ["document", "other"];

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/files", {
        params: {
          record_type: recordType,
          record_id: recordId,
          include_archived: true,
        },
      });
      setFiles(data.items || []);
    } catch (e) {
      toast.error(apiErr(e));
      setFiles([]);
    }
  }, [recordType, recordId]);

  useEffect(() => {
    load();
  }, [load]);

  const openPicker = () => inputRef.current?.click();

  const stageFiles = (fileList) => {
    const arr = Array.from(fileList || []);
    if (!arr.length) return;
    const existing = (files || []).map((f) => f.original_filename);
    setPending(
      arr.map((file) => ({
        file,
        name: file.name,
        dupe: existing.includes(file.name),
      })),
    );
    setMeta({
      category: categories[0],
      sensitivity: "standard",
      description: "",
      tags: "",
      expiry_date: "",
    });
    setProgress({});
    setUploadOpen(true);
  };

  const doUpload = async () => {
    setUploading(true);
    let ok = 0;
    for (let i = 0; i < pending.length; i++) {
      const { file } = pending[i];
      const fd = new FormData();
      fd.append("file", file);
      fd.append("record_type", recordType);
      fd.append("record_id", recordId);
      fd.append("category", meta.category || "document");
      fd.append("sensitivity", meta.sensitivity);
      fd.append("description", meta.description);
      fd.append("tags", meta.tags);
      fd.append("expiry_date", meta.expiry_date);
      try {
        await api.post("/files/upload", fd, {
          headers: { "Content-Type": "multipart/form-data" },
          onUploadProgress: (e) => {
            const pct = e.total ? Math.round((e.loaded * 100) / e.total) : 0;
            setProgress((p) => ({ ...p, [i]: pct }));
          },
        });
        ok++;
        setProgress((p) => ({ ...p, [i]: 100 }));
      } catch (e) {
        setProgress((p) => ({ ...p, [i]: -1 }));
        toast.error(`${file.name}: ${apiErr(e)}`);
      }
    }
    setUploading(false);
    if (ok) {
      toast.success(`${ok} file${ok > 1 ? "s" : ""} uploaded`);
    }
    setUploadOpen(false);
    setPending([]);
    load();
  };

  const openFile = async (f, action) => {
    try {
      const { data } = await api.post(`/files/${f.id}/signed-url`, null, {
        params: { action },
      });
      window.open(`${BACKEND}${data.url}`, "_blank", "noopener");
    } catch (e) {
      toast.error(apiErr(e));
    }
  };

  const onReplacePick = async (file) => {
    if (!file || !replaceTarget) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/files/${replaceTarget.id}/replace`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("New version uploaded");
      load();
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setReplaceTarget(null);
      if (replaceRef.current) replaceRef.current.value = "";
    }
  };

  const saveEdit = async () => {
    try {
      await api.patch(`/files/${editTarget.id}`, {
        category: editMeta.category,
        description: editMeta.description,
        tags: (editMeta.tags || "")
          .split(",")
          .map((t) => t.trim())
          .filter(Boolean),
        verification_status: editMeta.verification_status,
        expiry_date: editMeta.expiry_date || null,
      });
      toast.success("Details updated");
      setEditTarget(null);
      load();
    } catch (e) {
      toast.error(apiErr(e));
    }
  };

  const setArchived = async (f, archived) => {
    try {
      await api.post(`/files/${f.id}/${archived ? "archive" : "restore"}`);
      toast.success(archived ? "Archived" : "Restored");
      load();
    } catch (e) {
      toast.error(apiErr(e));
    }
  };

  const confirmDelete = async () => {
    try {
      if (purge)
        await api.delete(`/files/${deleteTarget.id}/permanent`, {
          params: { confirm: true },
        });
      else await api.delete(`/files/${deleteTarget.id}`);
      toast.success(purge ? "Permanently deleted" : "File deleted");
      setDeleteTarget(null);
      setPurge(false);
      load();
    } catch (e) {
      toast.error(apiErr(e));
    }
  };

  const doShare = async () => {
    try {
      await api.post(`/files/${shareTarget.id}/share`, share);
      toast.success(`Shared with ${share.audience}`);
      setShareTarget(null);
      setShare({ audience: "client", recipient: "", expires_in_days: 7 });
      load();
    } catch (e) {
      toast.error(apiErr(e));
    }
  };

  return (
    <Card className="p-5" data-testid="file-manager">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="font-display font-semibold">{title}</div>
          <p className="text-xs text-muted-foreground mt-0.5">
            Private object storage · access is logged and permission-checked
          </p>
        </div>
        {canWrite && (
          <Button
            size="sm"
            onClick={openPicker}
            data-testid="file-upload-trigger"
            className="gap-1.5 bg-[#255058] hover:bg-[#1c3d43] text-white"
          >
            <UploadCloud className="h-4 w-4" /> Upload
          </Button>
        )}
      </div>

      <input
        ref={inputRef}
        type="file"
        multiple
        hidden
        data-testid="file-input"
        onChange={(e) => {
          stageFiles(e.target.files);
          e.target.value = "";
        }}
      />
      <input
        ref={replaceRef}
        type="file"
        hidden
        data-testid="file-replace-input"
        onChange={(e) => onReplacePick(e.target.files?.[0])}
      />

      {canWrite && (
        <div
          onDragOver={(e) => {
            e.preventDefault();
            setDragging(true);
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragging(false);
            stageFiles(e.dataTransfer.files);
          }}
          onClick={openPicker}
          data-testid="file-dropzone"
          className={`mb-4 rounded-xl border-2 border-dashed cursor-pointer text-center py-7 transition-colors ${dragging ? "border-[#255058] bg-[#255058]/5" : "border-border hover:border-[#255058]/50"}`}
        >
          <UploadCloud className="h-6 w-6 mx-auto text-[#255058]" />
          <div className="text-sm mt-2 font-medium">
            Drag &amp; drop files here, or click to browse
          </div>
          <div className="text-xs text-muted-foreground mt-0.5">
            PDF, Office, images, audio &amp; video · category size limits apply
          </div>
        </div>
      )}

      {files === null ? (
        <div className="space-y-2">
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} className="h-14" />
          ))}
        </div>
      ) : files.length === 0 ? (
        <EmptyState
          icon={FileIcon}
          title="No files yet"
          description={
            canWrite
              ? "Upload documents, images or media to attach them to this record."
              : "Nothing has been shared here yet."
          }
        />
      ) : (
        <div className="space-y-2" data-testid="file-list">
          {files.map((f) => {
            const Icon = iconFor(f.extension);
            const quarantined = f.scan_status === "quarantined";
            const archived = f.status === "archived";
            return (
              <div
                key={f.id}
                data-testid={`file-row-${f.id}`}
                className={`flex items-center gap-3 p-3 rounded-lg border border-border ${archived ? "opacity-60" : ""}`}
              >
                <div className="h-10 w-10 rounded-lg grid place-items-center bg-accent shrink-0">
                  <Icon className="h-5 w-5 text-[#255058]" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium truncate max-w-[240px]">
                      {f.original_filename}
                    </span>
                    <Badge variant="secondary" className="text-[10px]">
                      {fmtLabel(f.category)}
                    </Badge>
                    {f.sensitive && (
                      <Badge className="text-[10px] bg-amber-100 text-amber-800 border-0 gap-1">
                        <ShieldAlert className="h-3 w-3" />
                        Sensitive
                      </Badge>
                    )}
                    {f.verification_status === "verified" && (
                      <Badge className="text-[10px] bg-emerald-100 text-emerald-700 border-0 gap-1">
                        <ShieldCheck className="h-3 w-3" />
                        Verified
                      </Badge>
                    )}
                    {quarantined && (
                      <Badge className="text-[10px] bg-red-100 text-red-700 border-0 gap-1">
                        <ShieldAlert className="h-3 w-3" />
                        Quarantined
                      </Badge>
                    )}
                    {archived && (
                      <Badge variant="outline" className="text-[10px]">
                        Archived
                      </Badge>
                    )}
                    {f.version_count > 1 && (
                      <Badge variant="outline" className="text-[10px]">
                        v{f.version}
                      </Badge>
                    )}
                  </div>
                  <div className="text-xs text-muted-foreground mt-0.5 truncate">
                    {fmtSize(f.size)} · {f.uploaded_by_name || "—"} ·{" "}
                    {fmtDate(f.created_at)}
                    {f.expiry_date && (
                      <span className="text-amber-600">
                        {" "}
                        · <Clock className="h-3 w-3 inline" /> expires{" "}
                        {fmtDate(f.expiry_date)}
                      </span>
                    )}
                  </div>
                  {(f.tags || []).length > 0 && (
                    <div className="flex gap-1 mt-1">
                      {f.tags.map((t) => (
                        <span
                          key={t}
                          className="text-[10px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground"
                        >
                          {t}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  {!quarantined && (
                    <Button
                      size="sm"
                      variant="ghost"
                      className="gap-1.5"
                      onClick={() => openFile(f, "view")}
                      data-testid={`file-view-${f.id}`}
                    >
                      <Eye className="h-4 w-4" /> View
                    </Button>
                  )}
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-8 w-8"
                        data-testid={`file-menu-${f.id}`}
                      >
                        <MoreVertical className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      {!quarantined && (
                        <DropdownMenuItem
                          onClick={() => openFile(f, "download")}
                        >
                          <Download className="h-4 w-4 mr-2" />
                          Download
                        </DropdownMenuItem>
                      )}
                      {f.version_count > 1 && (
                        <DropdownMenuItem onClick={() => setVersionsTarget(f)}>
                          <History className="h-4 w-4 mr-2" />
                          Version history
                        </DropdownMenuItem>
                      )}
                      {canWrite && (
                        <>
                          <DropdownMenuItem
                            onClick={() => {
                              setEditTarget(f);
                              setEditMeta({
                                category: f.category,
                                description: f.description || "",
                                tags: (f.tags || []).join(", "),
                                verification_status:
                                  f.verification_status || "unverified",
                                expiry_date: f.expiry_date || "",
                              });
                            }}
                          >
                            <Pencil className="h-4 w-4 mr-2" />
                            Edit details
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => {
                              setReplaceTarget(f);
                              replaceRef.current?.click();
                            }}
                          >
                            <RefreshCw className="h-4 w-4 mr-2" />
                            Replace (new version)
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => setShareTarget(f)}>
                            <Share2 className="h-4 w-4 mr-2" />
                            Share externally
                          </DropdownMenuItem>
                          {archived ? (
                            <DropdownMenuItem
                              onClick={() => setArchived(f, false)}
                            >
                              <ArchiveRestore className="h-4 w-4 mr-2" />
                              Restore
                            </DropdownMenuItem>
                          ) : (
                            <DropdownMenuItem
                              onClick={() => setArchived(f, true)}
                            >
                              <Archive className="h-4 w-4 mr-2" />
                              Archive
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            className="text-red-600"
                            onClick={() => {
                              setDeleteTarget(f);
                              setPurge(false);
                            }}
                          >
                            <Trash2 className="h-4 w-4 mr-2" />
                            Delete
                          </DropdownMenuItem>
                        </>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Upload dialog */}
      <Dialog
        open={uploadOpen}
        onOpenChange={(o) => !uploading && setUploadOpen(o)}
      >
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              Upload {pending.length} file{pending.length > 1 ? "s" : ""}
            </DialogTitle>
            <DialogDescription>
              Set the category and details applied to these files.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {pending.some((p) => p.dupe) && (
              <div className="flex items-start gap-2 text-xs text-amber-700 bg-amber-50 rounded-lg p-2.5">
                <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />A file with
                the same name already exists on this record. Uploading will
                create a separate file.
              </div>
            )}
            <div className="max-h-32 overflow-auto space-y-1.5">
              {pending.map((p, i) => (
                <div key={i} className="text-sm flex items-center gap-2">
                  <FileIcon className="h-4 w-4 text-muted-foreground shrink-0" />
                  <span className="truncate flex-1">{p.name}</span>
                  {uploading && progress[i] === -1 && (
                    <span className="text-xs text-red-600">failed</span>
                  )}
                  {uploading && progress[i] >= 0 && (
                    <div className="w-24">
                      <Progress value={progress[i] || 0} className="h-1.5" />
                    </div>
                  )}
                </div>
              ))}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium">Category</label>
                <Select
                  value={meta.category}
                  onValueChange={(v) => setMeta({ ...meta, category: v })}
                >
                  <SelectTrigger data-testid="upload-category" className="mt-1">
                    <SelectValue placeholder="Category" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map((c) => (
                      <SelectItem key={c} value={c}>
                        {fmtLabel(c)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs font-medium">Sensitivity</label>
                <Select
                  value={meta.sensitivity}
                  onValueChange={(v) => setMeta({ ...meta, sensitivity: v })}
                >
                  <SelectTrigger
                    data-testid="upload-sensitivity"
                    className="mt-1"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="standard">Standard</SelectItem>
                    <SelectItem value="sensitive">Sensitive</SelectItem>
                    <SelectItem value="confidential">Confidential</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <Input
              placeholder="Tags (comma separated)"
              value={meta.tags}
              onChange={(e) => setMeta({ ...meta, tags: e.target.value })}
              data-testid="upload-tags"
            />
            <div>
              <label className="text-xs font-medium">
                Expiry date (optional)
              </label>
              <Input
                type="date"
                value={meta.expiry_date}
                onChange={(e) =>
                  setMeta({ ...meta, expiry_date: e.target.value })
                }
                data-testid="upload-expiry"
                className="mt-1"
              />
            </div>
            <Textarea
              placeholder="Description (optional)"
              value={meta.description}
              onChange={(e) =>
                setMeta({ ...meta, description: e.target.value })
              }
              data-testid="upload-description"
            />
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setUploadOpen(false)}
              disabled={uploading}
            >
              Cancel
            </Button>
            <Button
              onClick={doUpload}
              disabled={uploading}
              data-testid="upload-confirm"
              className="gap-1.5 bg-[#255058] hover:bg-[#1c3d43] text-white"
            >
              {uploading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <UploadCloud className="h-4 w-4" />
              )}{" "}
              Upload
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit dialog */}
      <Dialog
        open={!!editTarget}
        onOpenChange={(o) => !o && setEditTarget(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit file details</DialogTitle>
          </DialogHeader>
          {editTarget && (
            <div className="space-y-3">
              <div>
                <label className="text-xs font-medium">Category</label>
                <Select
                  value={editMeta.category}
                  onValueChange={(v) =>
                    setEditMeta({ ...editMeta, category: v })
                  }
                >
                  <SelectTrigger className="mt-1" data-testid="edit-category">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map((c) => (
                      <SelectItem key={c} value={c}>
                        {fmtLabel(c)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs font-medium">
                  Verification status
                </label>
                <Select
                  value={editMeta.verification_status}
                  onValueChange={(v) =>
                    setEditMeta({ ...editMeta, verification_status: v })
                  }
                >
                  <SelectTrigger
                    className="mt-1"
                    data-testid="edit-verification"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="unverified">Unverified</SelectItem>
                    <SelectItem value="verified">Verified</SelectItem>
                    <SelectItem value="rejected">Rejected</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Input
                placeholder="Tags (comma separated)"
                value={editMeta.tags}
                onChange={(e) =>
                  setEditMeta({ ...editMeta, tags: e.target.value })
                }
              />
              <div>
                <label className="text-xs font-medium">Expiry date</label>
                <Input
                  type="date"
                  value={editMeta.expiry_date || ""}
                  onChange={(e) =>
                    setEditMeta({ ...editMeta, expiry_date: e.target.value })
                  }
                  className="mt-1"
                />
              </div>
              <Textarea
                placeholder="Description"
                value={editMeta.description}
                onChange={(e) =>
                  setEditMeta({ ...editMeta, description: e.target.value })
                }
              />
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditTarget(null)}>
              Cancel
            </Button>
            <Button
              onClick={saveEdit}
              data-testid="edit-save"
              className="bg-[#255058] hover:bg-[#1c3d43] text-white"
            >
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Version history */}
      <Dialog
        open={!!versionsTarget}
        onOpenChange={(o) => !o && setVersionsTarget(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Version history</DialogTitle>
            <DialogDescription>
              {versionsTarget?.original_filename}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between p-2 rounded bg-accent">
              <span>Current · v{versionsTarget?.version}</span>
              <span className="text-muted-foreground">
                {fmtSize(versionsTarget?.size)} ·{" "}
                {fmtDate(versionsTarget?.created_at)}
              </span>
            </div>
            {(versionsTarget?.versions || [])
              .slice()
              .reverse()
              .map((v, i) => (
                <div
                  key={i}
                  className="flex justify-between p-2 rounded border border-border"
                >
                  <span>
                    v{v.version} · {v.original_filename}
                  </span>
                  <span className="text-muted-foreground">
                    {fmtSize(v.size)} · {fmtDate(v.uploaded_at)}
                  </span>
                </div>
              ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Share dialog */}
      <Dialog
        open={!!shareTarget}
        onOpenChange={(o) => !o && setShareTarget(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Share externally</DialogTitle>
            <DialogDescription>
              Internal files stay internal by default. Sharing is logged and can
              be revoked.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs font-medium">Share with</label>
              <Select
                value={share.audience}
                onValueChange={(v) => setShare({ ...share, audience: v })}
              >
                <SelectTrigger className="mt-1" data-testid="share-audience">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="client">Client</SelectItem>
                  <SelectItem value="candidate">Candidate</SelectItem>
                  <SelectItem value="worker">Worker</SelectItem>
                  <SelectItem value="vendor">Vendor</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Input
              placeholder="Recipient email"
              value={share.recipient}
              onChange={(e) =>
                setShare({ ...share, recipient: e.target.value })
              }
              data-testid="share-recipient"
            />
            <div>
              <label className="text-xs font-medium">Expires in (days)</label>
              <Input
                type="number"
                min={1}
                value={share.expires_in_days}
                onChange={(e) =>
                  setShare({
                    ...share,
                    expires_in_days: Number(e.target.value),
                  })
                }
                className="mt-1"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShareTarget(null)}>
              Cancel
            </Button>
            <Button
              onClick={doShare}
              disabled={!share.recipient}
              data-testid="share-confirm"
              className="bg-[#255058] hover:bg-[#1c3d43] text-white"
            >
              Share
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <Dialog
        open={!!deleteTarget}
        onOpenChange={(o) => {
          if (!o) {
            setDeleteTarget(null);
            setPurge(false);
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete file</DialogTitle>
            <DialogDescription>
              {deleteTarget?.original_filename}
            </DialogDescription>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            Soft delete moves this file out of view and is recoverable by an
            administrator. This action is audited.
          </p>
          {canPurge && (
            <label className="flex items-center gap-2 text-sm mt-2 p-2.5 rounded-lg border border-red-200 bg-red-50">
              <input
                type="checkbox"
                checked={purge}
                onChange={(e) => setPurge(e.target.checked)}
                data-testid="permanent-delete-check"
              />
              <span className="text-red-700">
                Permanently delete from storage (Super Admin) — this cannot be
                undone.
              </span>
            </label>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setDeleteTarget(null);
                setPurge(false);
              }}
            >
              Cancel
            </Button>
            <Button
              className="bg-red-600 hover:bg-red-700 text-white"
              onClick={confirmDelete}
              data-testid="delete-confirm"
            >
              {purge ? "Permanently delete" : "Delete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
